import { type ReactNode, useState } from 'react';
import { api, ApiError } from '@/api/client';
import { Icon } from './Icon';

export const PASSWORD_MIN = 12;

// What is wrong with a new password typed twice, while it is typed.
export const passwordProblem = (next: string, again: string): string | null =>
    next.length > 0 && next.length < PASSWORD_MIN ? `${PASSWORD_MIN} characters or more` : again && again !== next ? 'The two do not match' : null;

// One's own password: the current one, then the new one twice. Every other session ends.
export function PasswordDialog(props: { onClose: () => void; onDone: () => void }): ReactNode {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [again, setAgain] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const problem = passwordProblem(next, again);

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <form
                className="dialog float"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (problem || !current || !next || next !== again) {
                        return;
                    }
                    setBusy(true);
                    setError(null);
                    api.changePassword(current, next)
                        .then(props.onDone)
                        .catch((reason: unknown) => setError(reason instanceof ApiError ? reason.message : 'The server did not answer'))
                        .finally(() => setBusy(false));
                }}
            >
                <div className="sheet-head">
                    <div>
                        <h2>Change my password</h2>
                        <p>Your other sessions will sign out.</p>
                    </div>
                    <button type="button" className="closebtn" onClick={props.onClose} title="Close (esc)">
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div className="dialog-body">
                    <label>
                        <span className="micro">Current password</span>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={current}
                            onChange={(event) => setCurrent(event.target.value)}
                            autoFocus
                        />
                    </label>
                    <label>
                        <span className="micro">New password · {PASSWORD_MIN} characters or more</span>
                        <input type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} />
                    </label>
                    <label>
                        <span className="micro">New password, again</span>
                        <input type="password" autoComplete="new-password" value={again} onChange={(event) => setAgain(event.target.value)} />
                    </label>
                    {(problem || error) && <div className="form-error">{problem ?? error}</div>}
                    <div className="dialog-actions">
                        <button type="button" className="act" onClick={props.onClose}>
                            Cancel
                        </button>
                        <button className="act primary" disabled={busy || Boolean(problem) || !current || !next || next !== again}>
                            {busy ? 'Saving…' : 'Change it'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
